import React, {useEffect, useState} from 'react'
import {Button, Modal} from 'react-bootstrap'
import Select from 'react-select'
import clsx from 'clsx'

import styles from './DeleteConfirmationPopup.module.scss'
import {useTimezone} from '../hooks/useTimezone'
import DatePicker from 'react-datepicker'

const ConfirmationPopup = ({
  show,
  handleClose,
  Message,
  confirm,
  confirmBtnTitle,
  stopClose = true,
  withInputConfirmation,
  withIntervalConfirmation,
  subTitle,
  selectedBulk,
  isReschedule,
}: any) => {
  const [canDelete, setCanDelete] = useState(false)
  const [interval, setBulkInterval] = useState(0)
  const [scheduleAt, setScheduleAt] = useState<any>(null)
  const [resumeType, setResumeType] = useState<any>('now')
  const {getUserTimezoneNow, timezoneLabel} = useTimezone()
  const userTimezoneNow = getUserTimezoneNow()

  // Determine button type based on action
  const getButtonType = () => {
    if (withIntervalConfirmation) return 'resume' // Resume campaign
    if (isReschedule) return 'reschedule' // Reschedule campaign

    // Check message content for specific actions
    const messageText = Message?.toLowerCase() || ''
    if (messageText.includes('cancel')) return 'cancel'
    if (messageText.includes('pause')) return 'pause'
    if (messageText.includes('delete')) return 'delete'

    // Check button title for additional context
    const buttonText = confirmBtnTitle?.toLowerCase() || ''
    if (buttonText.includes('resume')) return 'resume'
    if (buttonText.includes('reschedule')) return 'reschedule'
    if (buttonText.includes('cancel')) return 'cancel'
    if (buttonText.includes('pause')) return 'pause'

    return 'delete' // Default to delete
  }

  const buttonType = getButtonType()
  const isPurpleButton = ['resume', 'reschedule'].includes(buttonType)
  const isRedButton = ['cancel', 'delete', 'pause'].includes(buttonType)

  useEffect(() => {
    setBulkInterval(selectedBulk?.interval)
  }, [selectedBulk])

  const options = [
    {value: 5, label: '5 Minutes'},
    {value: 10, label: '10 Minutes'},
    {value: 20, label: '20 Minutes'},
    {value: 60, label: '1 Hour'},
    {value: 720, label: '12 Hour'},
    {value: 1440, label: '24 Hour'},
  ]

  const handleDelete = () => {
    confirm({interval, scheduleAt})
    setCanDelete(false)
    setBulkInterval(0)
    if (stopClose) handleClose()
  }
  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        dialogClassName={clsx('modal-dialog-centered mw-600px', {
          'resume-campaign-modal': buttonType === 'resume',
          'reschedule-campaign-modal': buttonType === 'reschedule',
          'cancel-campaign-modal': buttonType === 'cancel',
          'delete-confirmation-modal': buttonType === 'delete',
          'pause-campaign-modal': buttonType === 'pause',
        })}
      >
        <Modal.Header className={clsx(styles.modalHeader, 'text-center', 'no-border')}>
          <div className={clsx(styles.iconBox, 'col-12')}>
            <i
              className={clsx('fa fa-exclamation', {
                [styles.yellowCircle]: isPurpleButton, // Resume/reschedule actions get yellow warning icon
                [styles.redCircle]: isRedButton, // Cancel/delete/pause actions get red danger icon
              })}
            ></i>
          </div>
        </Modal.Header>
        <Modal.Body className={clsx(styles.modalBody)}>
          <div className='text-center fs-3 mb-4'>{Message}</div>
          {withInputConfirmation && (
            <div className='mb-4'>
              <input
                type='text'
                className='form-control form-control-solid '
                placeholder='Delete Confirmation'
                name='query'
                onChange={(e) => {
                  if (e.target.value === 'DELETE') {
                    setCanDelete(true)
                  } else {
                    setCanDelete(false)
                  }
                }}
              />
            </div>
          )}

          {selectedBulk?.staggerId && (
            <div>
              <div className={clsx(styles.infoBox)}>
                <div className='d-flex justify-content-between'>
                  <span className='fw-semibold'>Previous Interval:</span>
                  <span className='fw-bold'>
                    {options.find((it) => it.value === selectedBulk?.interval)?.label}
                  </span>
                </div>
                <div className='d-flex justify-content-between'>
                  <span className='fw-semibold'>Total Recipients:</span>
                  <span className='fw-bold'>
                    {selectedBulk?.staggerId && selectedBulk?.totalRecipients
                      ? `${selectedBulk?.totalRecipients - selectedBulk?.completedRecipients} Left`
                      : selectedBulk?.recipients}
                  </span>
                </div>
                <div className='d-flex justify-content-between'>
                  <span className='fw-semibold'>Total Stagger:</span>
                  <span className='fw-bold'>
                    {selectedBulk?.staggerId && selectedBulk?.totalRecipients
                      ? `${selectedBulk?.scheduledCount} Left`
                      : '-'}
                  </span>
                </div>
              </div>

              {/* Option for direct resume or reschedule */}
              {[
                {value: 'now', label: 'Send back log now and resume current configuration'},
                {value: 'later', label: 'Reset sending schedule'},
              ].map((it) => (
                <div
                  key={it.value}
                  className={clsx(
                    styles.formCheck,
                    'form-check-custom',
                    'form-check-solid',
                    'align-items-center',
                    'mb-2'
                  )}
                >
                  <input
                    className={clsx(styles.formCheckInput)}
                    type='radio'
                    value={it.value}
                    name='role'
                    checked={it?.value === resumeType}
                    onChange={() => {
                      setResumeType(it.value)
                      if (it.value === 'now') {
                        setScheduleAt(0)
                        setBulkInterval(0)
                      }
                    }}
                  />
                  <label className='ms-5'>
                    <div className='fs-5 fw-bold' onClick={() => setResumeType(it.value)}>
                      {it.label}
                    </div>
                  </label>
                </div>
              ))}

              {/* Interval Send Update*/}
              {selectedBulk?.interval && resumeType === 'later' && (
                <div className='row'>
                  <div className='col-md-12'>
                    <label htmlFor='' className='mb-2' data-testid='lblSelectDateA'>
                      Select Date & Time
                      <span className=''>{timezoneLabel}</span>
                    </label>
                    <DatePicker
                      minDate={userTimezoneNow}
                      dateFormat='MMMM d, yyyy h:mm aa'
                      placeholderText='Select a date'
                      showTimeSelect
                      className='border-0 form-control form-control-lg form-control-solid col-md-6'
                      onChange={(e: any) => {
                        setScheduleAt(e)
                      }}
                      selected={scheduleAt}
                    />
                  </div>
                  <div className='col-md-12'>
                    <span>Select New Interval Time</span>
                    <Select
                      name='selector'
                      className='form-select bg-light-primary border-0'
                      onChange={(e: any) => {
                        setBulkInterval(e.value)
                      }}
                      defaultValue={options.find((el) => el.value === selectedBulk?.interval)}
                      placeholder='Select interval'
                      options={options}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {!selectedBulk?.staggerId && isReschedule && (
            <div className='row'>
              <div className='col-md-12'>
                <label htmlFor='' className='mb-2' data-testid='lblSelectDateA'>
                  Select Date & Time
                  <span className=''>{timezoneLabel}</span>
                </label>
                <DatePicker
                  minDate={userTimezoneNow}
                  dateFormat='MMMM d, yyyy h:mm aa'
                  placeholderText='Select a date'
                  showTimeSelect
                  className='border-0 form-control form-control-lg form-control-solid col-md-6'
                  onChange={(e: any) => {
                    setScheduleAt(e)
                  }}
                  selected={scheduleAt}
                />
              </div>
              {selectedBulk?.interval && (
                <div className='col-md-12'>
                  <span>Select New Interval Time</span>
                  <Select
                    name='selector'
                    className='form-select bg-light-primary border-0'
                    onChange={(e: any) => {
                      setBulkInterval(e.value)
                    }}
                    defaultValue={options.find((el) => el.value === selectedBulk?.interval)}
                    placeholder='Select interval'
                    options={options}
                  />
                </div>
              )}
            </div>
          )}

          {subTitle && <div className='text-center fs-5 mt-3'>{subTitle}</div>}
        </Modal.Body>
        <Modal.Footer className={clsx(styles.modalFooter, 'd-flex justify-content-center gap-3')}>
          <Button
            className={clsx(styles.deleteCancelBtn)}
            style={{background: 'transparent', color: 'black'}}
            onClick={() => {
              setCanDelete(false)
              setBulkInterval(0)
              setScheduleAt(0)
              handleClose()
            }}
          >
            No, Cancel
          </Button>
          <Button
            className={clsx(styles.deleteSuccessBtn, {
              [styles.btnPurple]: isPurpleButton,
              [styles.btnRed]: isRedButton,
            })}
            disabled={
              (withInputConfirmation && !canDelete) ||
              (selectedBulk?.interval && resumeType === 'later' && !scheduleAt) ||
              (isReschedule && !scheduleAt)
            }
            onClick={handleDelete}
          >
            {confirmBtnTitle ? confirmBtnTitle : 'Yes, Delete!'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default React.memo(ConfirmationPopup)
